/**
 * DictResCard — карточка ресурса (screen-21 resCard J:277-337, DICT-3),
 * зеркало DictConsCard без норм: Название, erow2 Филиал (Dropdown single
 * «Вся сеть» + живые филиалы; null = вся сеть) | Стоимость (money «Покупная
 * цена»); ДВА блока привязок BindBlock (Квесты, Услуги — multi-Dropdown:
 * тап = один, long-press = мультивыбор; строки имя + ✕); покупки только
 * Сумма + Медиа (pfrow.two). «Сохранить» = PUT полей + PUT bindings
 * {quest_ids, service_ids} → onSaved хоста; err загрузки — без форм.
 * Буфер правок — хостовый map p.buf (эталон: module-map editSt): unmount
 * карточки (collapse/фильтр/смена editId) правок не теряет; буфер умирает
 * только по «Сохранить»/«Отмена».
 */
import { useEffect, useState } from "react";
import type { DictPurchase, DictResource, ResCardWire } from "../../api/dictTypes";
import type { Branch, MasterClass, Quest } from "../../api/types";
import { Dropdown, NumPadPop } from "../../components/shared";
import { useDictCard } from "../../hooks/useDictCard";
import { BindBlock, type BindRow, type DictPop } from "./DictBlocks";
import { DictPurchases } from "./DictPurchases";
import { fmtNum } from "./PriceRow";
import { ActionPair } from "./QuestFormCells";
import cfg from "./config.module.css";
import qcss from "./quests.module.css";
import scss from "./services.module.css";
import css from "./dicts.module.css";

const NET = "Вся сеть";

export interface ResSt {
  n: string; br: string | null; cost: number; roundTo: number;
  qs: BindRow[]; ss: BindRow[]; qO: boolean; sO: boolean;
}

const initSt = (w: ResCardWire): ResSt => {
  const qs = w.consumers.quests.map((x) => ({ id: x.quest_id, name: x.name }));
  const ss = w.consumers.services.map((x) => ({ id: x.service_id, name: x.name }));
  return {
    n: w.resource.name, br: w.resource.branch_id ?? null, cost: w.resource.cost || 0, roundTo: w.resource.round_to || 0,
    qs, ss, qO: qs.length > 0, sO: ss.length > 0,
  };
};

export interface DictResCardProps {
  row: DictResource;
  branches: Branch[];
  quests: Quest[];
  services: MasterClass[];
  /** Хостовый буфер правок id → ResSt (переживает размонтирование). */
  buf: Record<string, ResSt | undefined>;
  onClose: () => void;
  /** use_count — пересчёт по сохранённым привязкам: ветка Д6 у ✕ сразу
   *  видит свежий K (паритет с DictConsCard). */
  onSaved: (patch: { name: string; cost: number; branch_id: string | null; round_to: number; use_count: number }) => void;
}

export function DictResCard(p: DictResCardProps) {
  const d = useDictCard("r", p.row.id);
  const [st, setSt] = useState<ResSt | null>(() => p.buf[p.row.id] ?? null);
  const [added, setAdded] = useState<DictPurchase[]>([]);
  const [pop, setPop] = useState<DictPop | null>(null);
  const [brDd, setBrDd] = useState<HTMLElement | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  useEffect(() => {
    if (d.resCard) setSt((old) => old ?? initSt(d.resCard as ResCardWire));
  }, [d.resCard]);
  /** Правки — через set2 с зеркалом в p.buf; drop — только сейв/«Отмена». */
  const set2 = (v: ResSt | ((o: ResSt) => ResSt)) => setSt((o) => {
    if (!o) return o;
    const nv = typeof v === "function" ? v(o) : v;
    p.buf[p.row.id] = nv;
    return nv;
  });
  const drop = () => { delete p.buf[p.row.id]; };
  const cancel = () => { drop(); p.onClose(); };

  const save = async () => {
    if (!st || saving || !d.resCard) return;
    setSaving(true);
    setSaveErr(null);
    const name = st.n.trim() || d.resCard.resource.name;
    /* 05.10: причина отказа — с сервера (dictErr), а не «ошибка сети» на любой ответ */
    const eF = await d.saveFields({ name, cost: st.cost, branch_id: st.br, round_to: st.roundTo });
    const eB = eF ?? await d.saveBindings({
      quest_ids: st.qs.map((r) => r.id),
      service_ids: st.ss.map((r) => r.id),
    });
    setSaving(false);
    if (!eB) {
      drop();
      p.onSaved({ name, cost: st.cost, branch_id: st.br, round_to: st.roundTo, use_count: st.qs.length + st.ss.length });
    } else setSaveErr(`Не сохранилось: ${eB}.`);
  };

  if (d.err) {
    return (
      <div className={scss.medit}>
        <div className={cfg.errHint}>Карточка не загрузилась — повторите.</div>
        <ActionPair okLabel="Повторить" onOk={d.reload} onCancel={cancel} />
      </div>
    );
  }
  if (!st) {
    return (
      <div className={scss.medit}>
        {[0, 1, 2].map((i) => <div key={i} className={`${cfg.sk} ${cfg.skTea}`} />)}
      </div>
    );
  }
  const brName = st.br ? p.branches.find((b) => b.id === st.br)?.name ?? st.br : NET;
  return (
    <div className={scss.medit}>
      <div className={qcss.gap2}>
        <span className={cfg.klbl}>Название</span>
        <input className={qcss.txt} value={st.n} onChange={(e) => set2({ ...st, n: e.target.value })} />
      </div>
      <div className={css.erow2}>
        <div className={css.ecell}>
          <span className={cfg.klbl}>Филиал</span>
          <button type="button" className={cfg.teNumbtn} onClick={(e) => setBrDd(e.currentTarget)}>
            {brName} <span className={`${css.chev} ${css.chev8}`}>▾</span>
          </button>
          <Dropdown
            open={!!brDd} anchor={brDd} multi={false} selectedIds={[st.br ?? "__net"]}
            items={[{ id: "__net", label: NET }, ...p.branches.map((b) => ({ id: b.id, label: b.name }))]}
            onClose={() => setBrDd(null)}
            onCommit={(ids) => {
              const id = ids[0];
              if (id == null) return;
              set2((o) => ({ ...o, br: id === "__net" ? null : String(id) }));
            }}
          />
        </div>
        <div className={css.ecell}>
          <span className={cfg.klbl}>Стоимость</span>
          <button type="button" className={`${cfg.teNumbtn} ${qcss.num}`}
            onClick={(e) => setPop({
              anchor: e.currentTarget, label: "Покупная цена", mode: "money", value: st.cost,
              decimals: 2,
              onCommit: (v) => set2((o) => ({ ...o, cost: v || 0 })),
            })}>
            ₽{fmtNum(st.cost)}
          </button>
        </div>
        {/* 02.09 (владелец): округление вверх до кратного (0 — нет) */}
        <div className={css.ecell}>
          <span className={cfg.klbl}>Округлять до</span>
          <button type="button" className={`${cfg.teNumbtn} ${qcss.num}`} title="Расход округляется вверх до кратного (0 — без округления)"
            onClick={(e) => setPop({
              anchor: e.currentTarget, label: "Округлять расход до кратного", mode: "int", unit: "шт", max: 999, value: st.roundTo,
              onCommit: (v) => set2((o) => ({ ...o, roundTo: Math.max(0, v || 0) })),
            })}>
            {st.roundTo > 0 ? `${st.roundTo} шт` : "нет"}
          </button>
        </div>
      </div>
      <BindBlock title="Квесты" open={st.qO} onToggle={() => set2({ ...st, qO: !st.qO })}
        rows={st.qs} dict={p.quests.map((q) => ({ id: q.id, label: q.name }))}
        onChange={(rows, opened) => set2({ ...st, qs: rows, qO: opened ? true : st.qO })} />
      <BindBlock title="Услуги" open={st.sO} onToggle={() => set2({ ...st, sO: !st.sO })}
        rows={st.ss} dict={p.services.map((m) => ({ id: m.id, label: m.name }))}
        onChange={(rows, opened) => set2({ ...st, ss: rows, sO: opened ? true : st.sO })} />
      <DictPurchases isCons={false} unit="шт"
        purchases={[...added, ...(d.resCard?.purchases ?? [])]}
        addPurchase={d.addPurchase} uploadMedia={d.uploadMedia}
        onAdded={(x) => setAdded((a) => [x, ...a])}
        onMedia={(pid, m) => setAdded((a) =>
          a.map((x) => (x.id === pid ? { ...x, media: [...x.media, ...m] } : x)))}
        openPop={setPop} />
      {saveErr ? <div className={cfg.errHint}>{saveErr}</div> : null}
      <div className={css.footPad}>
        <ActionPair okLabel={saving ? "Сохраняем…" : "Сохранить"} okDisabled={saving}
          onOk={() => void save()} onCancel={cancel} />
      </div>
      <NumPadPop
        open={!!pop} anchor={pop?.anchor ?? null} label={pop?.label} mode={pop?.mode}
        unit={pop?.unit} max={pop?.max} value={pop?.value} decimals={pop?.decimals}
        onCommit={(v) => pop?.onCommit(v)} onClose={() => setPop(null)}
      />
    </div>
  );
}
